import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "card" };
const _hoisted_2 = {
    key: 0,
    class: "px-3 lg:px-4 pt-3 pb-2"
};
const _hoisted_3 = { class: "flex justify-between items-center" };
const _hoisted_4 = { class: "font-semibold" };
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = { class: "my-1 text-muted collapsible-content" };
const _hoisted_7 = { class: "w-full relative aspect-4/3 sm:aspect-video lg:aspect-2/1" };
const _hoisted_8 = {
    key: 0,
    class: "absolute inset-0 flex justify-center items-center text-muted z-2"
};
import { ref, watch } from 'vue';
import { useDeferredChart } from '@ts/common/useDeferredChart';
import { ___, toFixedNumberStr } from '@ts/common/language';
import { closest } from '@ts/common/math';
import { kcalToG } from '@ts/common/nutrition';
import SvgVue from '@ts/components/utils/SvgVue.vue';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import {} from '@ts/common/datasets';
import {} from 'chart.js/auto';
export default /*@__PURE__*/ _defineComponent({
    __name: 'CompareChartSubstrate',
    props: {
        dataA: { default: undefined },
        dataB: { default: undefined },
        help: { type: Boolean, default: false },
        header: { type: Boolean, default: true },
        tooltip: { type: Boolean, default: true }
    },
    setup(__props) {
        Chart.register(annotationPlugin);
        const props = __props;
        const showHelp = ref(false);
        const showPlaceholder = ref(true);
        const chart = ref(null);
        const containerRef = ref(null);
        let chartObj = undefined;
        // Create the chart only once its container has a real (non-zero) size — see useDeferredChart.
        useDeferredChart(containerRef, {
            create: () => {
                if (props.dataA !== undefined && props.dataB !== undefined)
                    drawChart(props.dataA, props.dataB);
            },
            getChart: () => chartObj,
        });
        watch(() => [props.dataA, props.dataB], ([newA, newB]) => {
            if (newA !== undefined && newB !== undefined)
                drawChart(newA, newB);
        }, { deep: true });
        function sideAt(data, power, maxPower) {
            if (power > maxPower)
                return { pctCarb: null, absCarb: 0, absFat: 0 };
            const cm = data.chart_metabolism;
            const idx = closest(power, cm.power);
            const f = cm.energy_fat_per_hour[idx] ?? 0;
            const c = cm.energy_carbo_per_hour[idx] ?? 0;
            const total = f + c;
            return { pctCarb: total > 0 ? (c / total) * 100 : 0, absCarb: c, absFat: f };
        }
        function crossover(data, maxPower) {
            const cm = data.chart_metabolism;
            for (let i = 1; i < cm.power.length; i++) {
                if (cm.power[i] > maxPower)
                    break;
                if ((cm.energy_fat_per_hour[i] ?? 0) < (cm.energy_carbo_per_hour[i] ?? 0)) {
                    return cm.power[i].toFixed(0);
                }
            }
            return null;
        }
        function drawChart(dataA, dataB) {
            if (chart.value === null)
                return;
            const maxPower = Math.ceil(Math.max(dataA.threshold_abs, dataB.threshold_abs));
            const labels = [];
            const carbA = [];
            const carbB = [];
            const absCarbA = [];
            const absFatA = [];
            const absCarbB = [];
            const absFatB = [];
            for (let p = 0; p <= maxPower; p++) {
                const a = sideAt(dataA, p, maxPower);
                const b = sideAt(dataB, p, maxPower);
                labels.push(p.toFixed(0));
                carbA.push(a.pctCarb);
                carbB.push(b.pctCarb);
                absCarbA.push(a.absCarb);
                absFatA.push(a.absFat);
                absCarbB.push(b.absCarb);
                absFatB.push(b.absFat);
            }
            // Each energy-demand line stops at its own analysis' threshold (the
            // x-axis spans up to the larger of the two thresholds).
            const energyA = absFatA.map((f, i) => (i > dataA.threshold_abs ? null : f + absCarbA[i]));
            const energyB = absFatB.map((f, i) => (i > dataB.threshold_abs ? null : f + absCarbB[i]));
            const chartData = {
                labels,
                datasets: [
                    {
                        label: ___({ en: 'Carbohydrate share', de: 'Kohlenhydratanteil' }),
                        data: carbA,
                        borderColor: '#cdb663',
                        borderWidth: 2,
                        pointRadius: 0,
                        fill: false,
                        yAxisID: 'y',
                    },
                    {
                        label: ___({ en: 'Carbohydrate share (comp.)', de: 'Kohlenhydratanteil (vergl.)' }),
                        data: carbB,
                        borderColor: '#cdb663',
                        borderWidth: 2,
                        borderDash: [10, 5],
                        pointRadius: 0,
                        fill: false,
                        yAxisID: 'y',
                    },
                    {
                        label: ___({ en: 'Energy demand', de: 'Energiebedarf' }),
                        data: energyA,
                        borderColor: '#636362',
                        borderWidth: 2,
                        // borderDash: [6, 4],
                        pointRadius: 0,
                        fill: false,
                        yAxisID: 'y1',
                    },
                    {
                        label: ___({ en: 'Energy demand (comp.)', de: 'Energiebedarf (vergl.)' }),
                        data: energyB,
                        borderColor: '#636362',
                        borderWidth: 2,
                        // borderDash: [2, 3],
                        borderDash: [10, 5],
                        pointRadius: 0,
                        fill: false,
                        yAxisID: 'y1',
                    },
                ],
            };
            const crossoverA = crossover(dataA, maxPower);
            const crossoverB = crossover(dataB, maxPower);
            const chartOptions = {
                maintainAspectRatio: false,
                responsive: true,
                animation: false,
                events: props.tooltip ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] : [],
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: ___({ en: 'Power [W]', de: 'Leistung [W]' }),
                        },
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 12,
                        },
                    },
                    y: {
                        position: 'left',
                        min: 0,
                        max: 100,
                        title: {
                            display: true,
                            text: ___({ en: 'Carbohydrate share of energy supply [%]', de: 'Kohlenhydratanteil an der Energiebereitstellung [%]' }),
                        },
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y1: {
                        type: 'linear',
                        position: 'right',
                        min: 0,
                        title: {
                            display: true,
                            text: ___({ en: 'Energy demand [kcal/h]', de: 'Energiebedarf [kcal/h]' }),
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        ticks: {
                            autoSkip: true,
                        },
                    },
                },
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    ...(props.tooltip
                        ? {
                            tooltip: {
                                callbacks: {
                                    title: (context) => {
                                        return ___({
                                            en: 'At ' + context[0].label + ' W:',
                                            de: 'Bei ' + context[0].label + ' W:',
                                        });
                                    },
                                    label: (context) => {
                                        const idx = context.dataIndex;
                                        const di = context.datasetIndex;
                                        const v = context.parsed.y;
                                        if (v === null || v === undefined)
                                            return '';
                                        if (di === 2 || di === 3) {
                                            // Energy-demand lines (kcal/h)
                                            return context.dataset.label + ': ' + toFixedNumberStr(v, 0) + ' kcal/h';
                                        }
                                        const kcal = di === 0 ? absCarbA[idx] : absCarbB[idx];
                                        return (context.dataset.label +
                                            ': ' +
                                            toFixedNumberStr(v, 0) +
                                            ' % (' +
                                            toFixedNumberStr(kcalToG(kcal, 'carb'), 1) +
                                            ' g/h ⇔' +
                                            toFixedNumberStr(kcal, 0) +
                                            ' kcal/h)');
                                    },
                                    labelColor: (context) => {
                                        return {
                                            borderColor: 'rgba(0, 0, 0, 0)',
                                            backgroundColor: context.dataset.borderColor ?? 'rgba(0, 0, 0, 0)',
                                            borderWidth: 0,
                                        };
                                    },
                                },
                            },
                        }
                        : {
                            tooltip: {
                                enabled: false,
                            },
                        }),
                    annotation: {
                        common: {
                            drawTime: 'afterDatasetsDraw',
                        },
                        annotations: [
                            ...(crossoverA === null
                                ? []
                                : [
                                // {
                                //   id: 'crossoverA',
                                //   type: 'line',
                                //   scaleID: 'x',
                                //   value: crossoverA,
                                //   borderColor: '#9ca3af',
                                //   borderWidth: 2,
                                //   label: {
                                //     position: 'end',
                                //     backgroundColor: '#9ca3af',
                                //     content: ___({en: 'Crossover', de: 'Crossover'}),
                                //     display: true,
                                //   },
                                // },
                                ]),
                            ...(crossoverB === null
                                ? []
                                : [
                                // {
                                //   id: 'crossoverB',
                                //   type: 'line',
                                //   scaleID: 'x',
                                //   value: crossoverB,
                                //   borderColor: '#9ca3af',
                                //   borderDash: [10, 5],
                                // //   borderWidth: 1.5,
                                //   borderWidth: 2,
                                //   label: {
                                //     position: 'start',
                                //     backgroundColor: '#9ca3af',
                                //     content: ___({en: 'Crossover (comp.)', de: 'Crossover (vergl.)'}),
                                //     display: true,
                                //   },
                                // },
                                ]),
                        ],
                    },
                },
            };
            if (chartObj === undefined) {
                chartObj = new Chart(chart.value, {
                    type: 'line',
                    data: chartData,
                    options: chartOptions,
                });
                showPlaceholder.value = false;
            }
            else {
                chartObj.data = chartData;
                chartObj.options = chartOptions;
                chartObj.update();
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("div", _hoisted_1, [
                    (__props.header)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(___)({ en: 'Substrate Split', de: 'Substratverteilung' })), 1 /* TEXT */),
                                (__props.help)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        onClick: _cache[1] || (_cache[1] = ($event) => (showHelp.value = !showHelp.value)),
                                        type: "button",
                                        class: "btn text-gray-350 dark:text-gray-450 hover:text-primary-500",
                                        "aria-label": _unref(___)({ en: 'Toggle help', de: 'Hilfe ein- und ausblenden' }),
                                        title: _unref(___)({ en: 'Help', de: 'Hilfe' })
                                    }, [
                                        _cache[0] || (_setBlockTracking(-1, true),
                                            (_cache[0] = _createVNode(SvgVue, {
                                                class: "w-4 h-4",
                                                icon: "lucide/circle-question-mark"
                                            })).cacheIndex = 0,
                                            _setBlockTracking(1),
                                            _cache[0])
                                    ], 8 /* PROPS */, _hoisted_5))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            (__props.help)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(["collapsible", showHelp.value ? 'expanded' : ''])
                                }, [
                                    _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(___)({
                                        en: 'Carbohydrate share of energy supply up to the anaerobic threshold for both analyses. The complement to 100% is the fat share. Crossover marks where carbohydrates overtake fat.',
                                        de: 'Kohlenhydratanteil an der Energiebereitstellung bis zur anaeroben Schwelle für beide Analysen. Die Differenz zu 100% ist der Fettanteil. Crossover markiert, wo Kohlenhydrate das Fett ablösen.',
                                    })), 1 /* TEXT */)
                                ], 2 /* CLASS */))
                                : _createCommentVNode("v-if", true)
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", {
                            class: "absolute inset-0",
                            ref_key: "containerRef",
                            ref: containerRef
                        }, [
                            _createElementVNode("canvas", {
                                class: "w-full h-full",
                                ref_key: "chart",
                                ref: chart
                            }, null, 512 /* NEED_PATCH */)
                        ], 512 /* NEED_PATCH */),
                        (showPlaceholder.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _createElementVNode("div", null, [
                                    _cache[2] || (_setBlockTracking(-1, true),
                                        (_cache[2] = _createVNode(SvgVue, {
                                            class: "w-4 h-4",
                                            icon: "lucide/chart-line"
                                        })).cacheIndex = 2,
                                        _setBlockTracking(1),
                                        _cache[2]),
                                    _createTextVNode(" " + _toDisplayString(_unref(___)({ en: 'loading...', de: 'lädt...' })), 1 /* TEXT */)
                                ])
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ])
            ]));
        };
    }
});
